import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { extractError } from "@/lib/api";
import { Lightning, Ticket, Gift } from "@phosphor-icons/react";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", access_code: "" });
  const [showCode, setShowCode] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await register(form.name, form.email, form.password, form.access_code);
      if (data.active_tool_ids?.length > 0) {
        toast.success(`Welcome! Access to ${data.active_tool_ids.length} tool(s) unlocked.`);
      } else {
        toast.success("Account created!");
      }
      navigate(data.active_tool_ids?.length > 0 ? "/library" : "/store");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 hero-grid">
      <div className="w-full max-w-md">
        <div className="border border-border bg-card p-8 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Lightning size={20} weight="fill" className="text-primary" />
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">Register</span>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-2">Get started.</h1>
          <p className="text-sm text-muted-foreground mb-8">No credit card required. Add tools whenever.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-mono uppercase tracking-widest">Name</Label>
              <Input id="name" required data-testid="register-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black rounded-sm border-border focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest">Email</Label>
              <Input id="email" type="email" required autoComplete="email" data-testid="register-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black rounded-sm border-border focus-visible:ring-primary" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-widest">Password</Label>
              <Input id="password" type="password" required minLength={6} autoComplete="new-password" data-testid="register-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-black rounded-sm border-border focus-visible:ring-primary" />
              <p className="text-xs text-muted-foreground">Min 6 characters</p>
            </div>

            {!showCode ? (
              <button type="button" onClick={() => setShowCode(true)} data-testid="show-access-code-btn"
                      className="w-full flex items-center gap-2 justify-center px-3 py-2 border border-dashed border-primary/40 hover:border-primary/70 hover:bg-primary/5 rounded-sm text-sm text-primary transition-colors">
                <Gift size={14} weight="bold" /> Have an access code?
              </button>
            ) : (
              <div className="border border-primary/30 bg-primary/5 rounded-sm p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono uppercase tracking-widest text-primary flex items-center gap-1"><Ticket size={12} weight="bold" /> Redeem access code</span>
                  <button type="button" onClick={() => { setShowCode(false); setForm({ ...form, access_code: "" }); }} className="text-xs text-muted-foreground hover:text-destructive">Skip</button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="access_code" className="text-xs font-mono uppercase tracking-widest">Access code</Label>
                  <Input id="access_code" data-testid="register-access-code-input" value={form.access_code} onChange={(e) => setForm({ ...form, access_code: e.target.value.toUpperCase() })} placeholder="NIFTY2026" className="bg-black rounded-sm border-border focus-visible:ring-primary font-mono uppercase" />
                  <p className="text-xs text-muted-foreground">Provided by your instructor. Your email must be on the invite list to redeem.</p>
                </div>
              </div>
            )}

            <Button type="submit" disabled={busy} data-testid="register-submit-btn" className="w-full bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">
              {busy ? "Creating..." : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have one? <Link to="/login" data-testid="register-to-login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
